import { DataFlow, IntegrationType } from '../catalog/enums';
import { IntegrationIDs } from '../catalog/ids';
import type { SynthesizedIntegration } from './types';

/** A rendered card label — text plus an optional Pluma Badge color. */
export type IntegrationLabel = {
  text: string;
  color?: 'teal' | 'plum' | 'blue' | 'clementine';
};

/** The gate-relevant slice of a plan feature (`account.planFeatures[…]`). */
export type PlanFeatureGate = {
  enabled: boolean;
  showPremiumLabel: boolean;
};

export type PremiumGates = {
  cdpPremium: PlanFeatureGate;
  warehouseSync: PlanFeatureGate;
};

const MAX_DESCRIPTION_LENGTH = 100;

/** Warehouse data-out ids whose premium access is governed by `warehouse_sync` (not `cdp_premium_integrations`). */
const PREMIUM_WAREHOUSE_IDS: readonly string[] = [
  IntegrationIDs.amazonS3,
  IntegrationIDs.gcs,
  IntegrationIDs.azureBlob,
  IntegrationIDs.yandex,
  IntegrationIDs.snowflakeOut,
  IntegrationIDs.bigQueryOut,
  IntegrationIDs.redshiftOut,
];

export function isPremiumWarehouseIntegration(id: string | undefined): boolean {
  return id != null && PREMIUM_WAREHOUSE_IDS.includes(id);
}

/** Normalize the premium flag: manually-built cards set `premiumIntegration`; backend rows carry `is_premium`. */
export function isPremiumIntegration(
  integration: Pick<SynthesizedIntegration, 'premiumIntegration' | 'is_premium'>,
): boolean {
  return integration.premiumIntegration ?? integration.is_premium ?? false;
}

/**
 * Whether the card is behind the premium gate (clicking opens the upgrade
 * popover instead of navigating). Premium warehouse cards use `warehouse_sync`;
 * everything else uses `cdp_premium_integrations`.
 */
export function isFeatureGated(
  integration: Pick<SynthesizedIntegration, 'id' | 'premiumIntegration' | 'is_premium'>,
  gates: PremiumGates,
): boolean {
  if (isPremiumWarehouseIntegration(integration.id) && gates.warehouseSync.enabled) {
    return false;
  }
  return isPremiumIntegration(integration) && !gates.cdpPremium.enabled;
}

/** Whether the "Premium" label should show. */
export function showPremiumLabel(
  integration: Pick<SynthesizedIntegration, 'id' | 'premiumIntegration' | 'is_premium'>,
  gates: PremiumGates,
): boolean {
  if (isPremiumWarehouseIntegration(integration.id)) {
    return isPremiumIntegration(integration) && gates.warehouseSync.showPremiumLabel;
  }
  return isPremiumIntegration(integration) && gates.cdpPremium.showPremiumLabel;
}

/** The legacy tag is truthy for any non-empty, non-`'false'` `legacy` value. */
export function isLegacy(legacy: SynthesizedIntegration['legacy']): boolean {
  return legacy != null && String(legacy) !== '' && String(legacy).toLowerCase() !== 'false';
}

/** Build the card's label row (data-flow → partner → beta → premium → legacy). */
export function buildLabels(
  integration: Pick<
    SynthesizedIntegration,
    'dataFlow' | 'publicType' | 'beta' | 'legacy' | 'id' | 'premiumIntegration' | 'is_premium'
  >,
  gates: PremiumGates,
): IntegrationLabel[] {
  const labels: IntegrationLabel[] = [];

  if (integration.dataFlow === DataFlow.in) {
    labels.push({ text: 'Data in', color: 'teal' });
  } else if (integration.dataFlow === DataFlow.out) {
    labels.push({ text: 'Data out', color: 'plum' });
  } else if (integration.dataFlow === DataFlow.channel) {
    labels.push({ text: 'Channel', color: 'blue' });
  }
  if (integration.publicType === IntegrationType.connectPartner) {
    labels.push({ text: 'Partner', color: 'plum' });
  }
  if (integration.beta === true) {
    labels.push({ text: 'Beta' });
  }
  if (showPremiumLabel(integration, gates)) {
    labels.push({ text: 'Premium' });
  }
  if (isLegacy(integration.legacy)) {
    labels.push({ text: 'Legacy' });
  }
  return labels;
}

export function isDescriptionTruncated(description: string | undefined): boolean {
  return (description?.length ?? 0) > MAX_DESCRIPTION_LENGTH;
}

/**
 * Truncate to 100 chars on the last word boundary, appending an ellipsis. A prefix holding no space
 * is cut at the limit instead — the Ember source does `slice(0, lastIndexOf(' '))`, which silently
 * drops the last character when `lastIndexOf` returns -1.
 */
export function truncateDescription(description: string | undefined): string {
  const desc = description?.slice(0, MAX_DESCRIPTION_LENGTH) ?? '';
  if (!isDescriptionTruncated(description)) {
    return desc;
  }
  const lastSpace = desc.lastIndexOf(' ');
  return `${lastSpace === -1 ? desc : desc.slice(0, lastSpace)}…`;
}

export function isPipelineCard(cardType: string | undefined): boolean {
  return cardType?.startsWith('pipelines_') ?? false;
}

/** Cross-app Pipelines (CDP) deep-link for a `pipelines_<Type>_<option>` card. */
export function pipelinesUrl(cardType: string | undefined, workspaceId: string | number): string {
  const [, type, optionId] = (cardType ?? '').split('_');
  const paramType = (type ?? '').toLowerCase();
  // oxlint-disable-next-line local-rules/enforce-generate-path -- cross-app Pipelines (CDP) deep-link, not a Hydra router path
  const baseUrl = `/workspaces/${workspaceId}/pipelines/${paramType}s/add`;
  return optionId != null && optionId !== '' ? `${baseUrl}?${paramType}=${optionId}` : baseUrl;
}

/* ------------------------------------------------------------------------- */
/* Feature-gated popover copy                                                 */
/* ------------------------------------------------------------------------- */

export function featureGatedTitle(name: string | undefined, dataFlow: SynthesizedIntegration['dataFlow']): string {
  if (dataFlow === DataFlow.in) {
    return `Bring data from ${name} to Customer.io Premium`;
  }
  if (dataFlow === DataFlow.out) {
    return `Send data to ${name} with Customer.io Premium`;
  }
  return `Connect ${name} to Customer.io Premium`;
}

export function featureGatedText(
  name: string | undefined,
  dataFlow: SynthesizedIntegration['dataFlow'],
  isTrial: boolean,
): string {
  const base = dataFlow === DataFlow.in ? `Bring data from ${name}` : `Send data to ${name}`;
  return `${base} with a Premium ${isTrial ? 'trial' : 'plan'}. Try it risk-free for 14 days.`;
}

/* ------------------------------------------------------------------------- */
/* Connector route resolution                                                 */
/* ------------------------------------------------------------------------- */

/**
 * Map an integration's route name to the embedded Journeys-app URL that serves
 * the connector detail page. `:type` is filled from the card's dynamic segment.
 */
const CONNECTOR_ROUTE_PATHS: Record<string, string> = {
  'integrations.type': 'integrations/:type',
  'integrations.data_out.type': 'integrations/data_out/:type',
  'integrations.warehouse.type': 'integrations/warehouse/:type',
  'integrations.warehouse.snowflake': 'integrations/warehouse/snowflake',
  'integrations.warehouse.bigquery': 'integrations/warehouse/bigquery',
  'integrations.warehouse.redshift': 'integrations/warehouse/redshift',
  'integrations.audiences.type.setup': 'integrations/audiences/:type/setup',
  'integrations.audiences.type.settings': 'integrations/audiences/:type/settings',
  'integrations.forms.type': 'integrations/forms/:type',
  'integrations.reporting-webhooks.new': 'integrations/reporting-webhooks/new',
  'integrations.segment_in': 'integrations/segment_in',
  'integrations.segment_out': 'integrations/segment_out',
  'integrations.mparticle_out': 'integrations/mparticle_out',
  'integrations.cdp_in': 'integrations/cdp_in',
  'integrations.cdp_out': 'integrations/cdp_out',
  'integrations.customerio': 'integrations/customerio',
  'sql-imports': 'integrations/sql-sync/:type',
  'actions.email.sending_domains': 'settings/actions/email/sending_domains',
  'actions.push': 'settings/actions/push',
  'actions.in_app': 'settings/actions/in_app',
  'actions.url_params': 'settings/actions/url_params',
  'actions.line': 'settings/actions/line',
  'actions.twilio': 'settings/actions/twilio',
  'actions.slack': 'settings/actions/slack',
  'actions.whatsapp': 'settings/actions/whatsapp',
};

/** Backend catalog rows carry no route name; NativeDataOut routes to data_out, everything else to the generic connector page. */
function connectorRouteName(integration: Pick<SynthesizedIntegration, 'path' | 'type'>): string {
  if (integration.path != null && integration.path !== '') {
    return integration.path;
  }
  return integration.type === IntegrationType.nativeDataOut ? 'integrations.data_out.type' : 'integrations.type';
}

/** Backend catalog rows use their id as the dynamic segment (the wire model aliases `dynamicSegment` to `id`). */
function connectorDynamicSegment(integration: Pick<SynthesizedIntegration, 'dynamicSegment' | 'path' | 'id'>): string {
  if (integration.dynamicSegment != null && integration.dynamicSegment !== '') {
    return integration.dynamicSegment;
  }
  return integration.path == null || integration.path === '' ? integration.id : '';
}

/**
 * Resolve the href a directory card navigates to:
 * - external → the literal URL on `path`
 * - pipelines card → the cross-app Pipelines deep-link
 * - otherwise → the embedded Journeys-app connector URL
 *
 * Feature-gated cards render a popover instead and never call this.
 */
export function resolveCardHref(
  integration: Pick<SynthesizedIntegration, 'id' | 'path' | 'type' | 'dynamicSegment' | 'external'>,
  cardType: string | undefined,
  workspaceId: string | number,
): string {
  if (integration.external === true) {
    return integration.path ?? '';
  }
  if (isPipelineCard(cardType)) {
    return pipelinesUrl(cardType, workspaceId);
  }

  const template = CONNECTOR_ROUTE_PATHS[connectorRouteName(integration)];
  const dynamicSegment = connectorDynamicSegment(integration);
  const path = (template ?? 'integrations').replace(':type', dynamicSegment);
  // oxlint-disable-next-line local-rules/enforce-generate-path -- embedded Journeys-app connector deep-link, not a Hydra router path
  return `/workspaces/${workspaceId}/journeys/${path}`;
}
